import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { ExpenseBankSelect } from "@/components/expenses/ExpenseBankSelect";
import {
  PaymentMethodFieldsGroup,
  emptyPaymentMethodDraft,
  paymentMethodUsesBank,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import type { SalePaymentMethod, SaleTokenType } from "@/lib/sales/salesStore";
import { cn } from "@/lib/utils";
import { Calculator, CheckCircle2, DollarSign, HandCoins, Layers, Tag, Wallet } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

export type SalePaymentType = "token" | "full" | "partial" | "installment";

export type SalePaymentEntry = {
  id: string;
  paymentMode: SalePaymentType;
  amount: number;
  paymentDate: string;
  paymentMethod: SalePaymentMethod;
  bankId?: string;
  bankName?: string;
  transactionReferenceNo?: string;
  payOrderNo?: string;
  chequeNo?: string;
  depositSlipNo?: string;
  chequeDate?: string;
  tokenType?: SaleTokenType;
  payableDate?: string;
  installmentMonths?: number;
  installmentFirstDueDate?: string;
  notes?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salePrice: number;
  remainingBalance: number;
  customerName?: string;
  productName?: string;
  editingEntry?: SalePaymentEntry | null;
  disabled?: boolean;
  /** When false, hides the Installment payment-line mode (sale-level Cash/Installment selector owns that). */
  allowInstallmentMode?: boolean;
  isInstallmentSale?: boolean;
  onSave: (entry: SalePaymentEntry) => void;
};

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function ConfigureSalePaymentDialog({
  open,
  onOpenChange,
  salePrice,
  remainingBalance,
  customerName,
  productName,
  editingEntry,
  disabled = false,
  allowInstallmentMode = true,
  isInstallmentSale = false,
  onSave,
}: Props) {
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [mode, setMode] = useState<SalePaymentType>("token");
  const [amountInput, setAmountInput] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [tokenType, setTokenType] = useState<SaleTokenType>("unconditional");
  const [payableDate, setPayableDate] = useState("");
  const [months, setMonths] = useState(3);
  const [firstDueDate, setFirstDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [methodDraft, setMethodDraft] = useState<PaymentMethodDraft>(() => emptyPaymentMethodDraft());
  const [error, setError] = useState<string | null>(null);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchBanks()
      .then((rows) => {
        if (!cancelled) setBanks(rows);
      })
      .catch(() => {
        if (!cancelled) setBanks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Max allowed for this line is remainingBalance + (if editing, the existing amount of this line)
  const maxAllowedAmount = useMemo(() => {
    const base = editingEntry ? remainingBalance + editingEntry.amount : remainingBalance;
    return Math.max(0, Math.min(salePrice, Math.round(base * 100) / 100));
  }, [editingEntry, remainingBalance, salePrice]);

  useEffect(() => {
    if (!open) return;
    setError(null);

    if (editingEntry) {
      setMode(editingEntry.paymentMode);
      setAmountInput(editingEntry.amount > 0 ? String(editingEntry.amount) : "");
      setPaymentDate(editingEntry.paymentDate || new Date().toISOString().split("T")[0]);
      setTokenType(editingEntry.tokenType || "unconditional");
      setPayableDate(editingEntry.payableDate || "");
      setMonths(editingEntry.installmentMonths || 3);
      setFirstDueDate(editingEntry.installmentFirstDueDate || "");
      setNotes(editingEntry.notes || "");
      setMethodDraft({
        paymentMethod: editingEntry.paymentMethod || "cash",
        bankId: editingEntry.bankId || "",
        transactionReferenceNo: editingEntry.transactionReferenceNo || "",
        payOrderNo: editingEntry.payOrderNo || "",
        chequeNo: editingEntry.chequeNo || "",
        depositSlipNo: editingEntry.depositSlipNo || "",
        chequeDate: editingEntry.chequeDate || "",
      });
    } else {
      const defaultMode = isInstallmentSale
        ? "installment"
        : maxAllowedAmount === salePrice && salePrice > 0
        ? "token"
        : "partial";
      setMode(defaultMode);
      const defaultAmount = maxAllowedAmount > 0 ? String(maxAllowedAmount) : "";
      setAmountInput(defaultAmount);
      setPaymentDate(new Date().toISOString().split("T")[0]);
      setTokenType("unconditional");
      setPayableDate("");
      setMonths(3);
      setFirstDueDate("");
      setNotes(isInstallmentSale ? "Installment Down Payment" : "");
      setMethodDraft(emptyPaymentMethodDraft({ paymentMethod: "cash" }));
    }
  }, [open, editingEntry, maxAllowedAmount, salePrice, allowInstallmentMode, isInstallmentSale]);

  useEffect(() => {
    if (!allowInstallmentMode && !isInstallmentSale && mode === "installment") {
      setMode(maxAllowedAmount === salePrice && salePrice > 0 ? "token" : "partial");
    }
  }, [allowInstallmentMode, isInstallmentSale, mode, maxAllowedAmount, salePrice]);

  const enteredAmount = useMemo(() => {
    if (mode === "full") return maxAllowedAmount;
    return parseAmount(amountInput) ?? 0;
  }, [mode, maxAllowedAmount, amountInput]);

  const projectedRemainingBalance = useMemo(() => {
    return Math.max(0, Math.round((maxAllowedAmount - enteredAmount) * 100) / 100);
  }, [maxAllowedAmount, enteredAmount]);

  const paidPercent = useMemo(() => {
    if (salePrice <= 0) return 0;
    const totalEffectivePaid = (salePrice - maxAllowedAmount) + enteredAmount;
    return Math.min(100, Math.round((totalEffectivePaid / salePrice) * 100));
  }, [salePrice, maxAllowedAmount, enteredAmount]);

  const enteredAmountLabel = enteredAmount > 0 ? pkrAmountShortLabel(enteredAmount) : "";

  const handleModeSelect = (newMode: SalePaymentType) => {
    setMode(newMode);
    setError(null);
    if (newMode === "full" || (isInstallmentSale && newMode === "installment")) {
      setAmountInput(maxAllowedAmount > 0 ? String(maxAllowedAmount) : "");
      setPayableDate("");
    }
  };

  const applyPreset = (percent: number) => {
    if (maxAllowedAmount <= 0) return;
    const value = Math.round(maxAllowedAmount * (percent / 100) * 100) / 100;
    setAmountInput(String(value));
    setError(null);
  };

  const handleSave = () => {
    if (salePrice <= 0) {
      toast.error("Please specify a valid sale price first.");
      return;
    }

    const amountToSave = mode === "full" ? maxAllowedAmount : enteredAmount;

    if (amountToSave <= 0) {
      setError("Enter a valid payment amount greater than zero.");
      return;
    }

    if (amountToSave > maxAllowedAmount + 0.01) {
      setError(`Payment cannot exceed the remaining balance of ${formatCurrency(maxAllowedAmount)}.`);
      return;
    }

    if (!paymentDate.trim()) {
      setError("Payment date is required.");
      return;
    }

    if (mode === "installment" && !isInstallmentSale) {
      if (projectedRemainingBalance <= 0) {
        setError("Installment plan requires a remaining balance greater than zero.");
        return;
      }
      if (!firstDueDate.trim()) {
        setError("Select the first installment due date.");
        return;
      }
    }

    const bank = banks.find((b) => String(b.id) === methodDraft.bankId);
    const entry: SalePaymentEntry = {
      id: editingEntry?.id || `pay_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      paymentMode: isInstallmentSale && (mode === "full" || mode === "installment") ? "installment" : mode,
      amount: amountToSave,
      paymentDate,
      paymentMethod: (methodDraft.paymentMethod as SalePaymentMethod) || "cash",
      bankId: methodDraft.bankId,
      bankName: bank?.name ?? "",
      transactionReferenceNo: methodDraft.transactionReferenceNo,
      payOrderNo: methodDraft.payOrderNo,
      chequeNo: methodDraft.chequeNo,
      depositSlipNo: methodDraft.depositSlipNo,
      chequeDate: methodDraft.chequeDate,
      tokenType: mode === "token" ? tokenType : undefined,
      payableDate: mode === "token" ? payableDate : undefined,
      installmentMonths: mode === "installment" && !isInstallmentSale ? months : undefined,
      installmentFirstDueDate: mode === "installment" && !isInstallmentSale ? firstDueDate : undefined,
      notes: notes.trim() || (isInstallmentSale && (mode === "installment" || mode === "full") ? "Installment Down Payment" : undefined),
    };

    onSave(entry);
    toast.success(editingEntry ? "Payment line updated." : "Payment line added.");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        {/* Header */}
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl border border-primary/20 bg-primary/10 flex items-center justify-center text-primary">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">
                {editingEntry ? "Edit Payment Line" : "Add Payment Line"}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                Record the payment type, received amount, dates, and payment method details.
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Profile / Context Card */}
        <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs mt-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded-full border border-primary/20 bg-primary/10 flex items-center justify-center font-bold text-xs shrink-0 text-primary">
              <Wallet className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-foreground truncate">{customerName || "Customer"}</p>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold">
                {productName || "Product Sale"}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0 pl-3">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground">
              {isInstallmentSale ? "Remaining Down Payment" : "Outstanding / Balance"}
            </p>
            <p className="font-bold text-primary text-sm">{formatCurrency(maxAllowedAmount)}</p>
          </div>
        </div>

        {/* 2-Column Split Body Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          {/* Left Column: Form Inputs */}
          <div className="space-y-4">
            {/* Payment Type Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">Payment Type *</Label>
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => handleModeSelect("token")}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 rounded-lg border p-2 text-center transition-all",
                    mode === "token"
                      ? "border-primary bg-primary/10 text-primary font-semibold shadow-xs"
                      : "border-border/60 bg-card/60 hover:bg-muted/30 text-muted-foreground"
                  )}
                >
                  <Tag className="h-3.5 w-3.5" />
                  <span className="text-[11px]">{isInstallmentSale ? "Token (Advance)" : "Token"}</span>
                </button>

                {isInstallmentSale ? (
                  <button
                    type="button"
                    onClick={() => handleModeSelect("installment")}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 rounded-lg border p-2 text-center transition-all",
                      mode === "installment" || mode === "full"
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold shadow-xs"
                        : "border-border/60 bg-card/60 hover:bg-muted/30 text-muted-foreground"
                    )}
                  >
                    <Wallet className="h-3.5 w-3.5" />
                    <span className="text-[11px]">Down Payment</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleModeSelect("full")}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 rounded-lg border p-2 text-center transition-all",
                      mode === "full"
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold shadow-xs"
                        : "border-border/60 bg-card/60 hover:bg-muted/30 text-muted-foreground"
                    )}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span className="text-[11px]">Full (100%)</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleModeSelect("partial")}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 rounded-lg border p-2 text-center transition-all",
                    mode === "partial"
                      ? "border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold shadow-xs"
                      : "border-border/60 bg-card/60 hover:bg-muted/30 text-muted-foreground"
                  )}
                >
                  <DollarSign className="h-3.5 w-3.5" />
                  <span className="text-[11px]">{isInstallmentSale ? "Partial Down Pay" : "Partial"}</span>
                </button>

                {!isInstallmentSale && allowInstallmentMode ? (
                  <button
                    type="button"
                    onClick={() => handleModeSelect("installment")}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 rounded-lg border p-2 text-center transition-all",
                      mode === "installment"
                        ? "border-purple-500 bg-purple-500/10 text-purple-600 dark:text-purple-400 font-semibold shadow-xs"
                        : "border-border/60 bg-card/60 hover:bg-muted/30 text-muted-foreground"
                    )}
                  >
                    <Layers className="h-3.5 w-3.5" />
                    <span className="text-[11px]">Installment</span>
                  </button>
                ) : null}
              </div>
            </div>

            {/* Payment Amount Field */}
            <div className="space-y-2">
              <div className="flex min-h-5 items-center justify-between gap-2">
                <Label htmlFor="line-payment-amount" className="text-xs font-semibold text-foreground">
                  {mode === "full"
                    ? "Full Balance Amount"
                    : mode === "installment"
                    ? "Down Payment / Token"
                    : mode === "partial"
                    ? "Partial Payment Amount"
                    : "Token Amount"}{" "}
                  ({getActiveCompanyCurrency().code}) <span className="text-destructive">*</span>
                </Label>
                {enteredAmountLabel ? (
                  <span className="shrink-0 text-xs font-bold tabular-nums text-primary">
                    {enteredAmountLabel}
                  </span>
                ) : null}
              </div>

              {mode !== "full" ? (
                <>
                  <div className="relative">
                    <Input
                      id="line-payment-amount"
                      className="glass-input h-10 pr-12 font-semibold"
                      value={amountInput}
                      disabled={disabled}
                      onChange={(e) => {
                        setAmountInput(e.target.value);
                        setError(null);
                      }}
                      placeholder="0.00"
                      inputMode="decimal"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                      {getActiveCompanyCurrency().code}
                    </span>
                  </div>
                  {/* Quick Preset Buttons */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <span className="text-[10px] text-muted-foreground self-center mr-1 font-medium">Quick:</span>
                    {[25, 50, 100].map((percent) => (
                      <button
                        key={percent}
                        type="button"
                        onClick={() => applyPreset(percent)}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                      >
                        {percent === 100 ? "Full" : `${percent}%`}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-10 px-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 flex items-center justify-between font-bold text-emerald-600 dark:text-emerald-400 tabular-nums text-sm">
                  <span>{formatCurrency(maxAllowedAmount)}</span>
                  <span className="text-xs bg-emerald-500/20 px-2 py-0.5 rounded text-emerald-700 dark:text-emerald-300">100% Upfront</span>
                </div>
              )}
            </div>

            {/* Date & Payment Method */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">Payment Date *</Label>
                <DatePartsPicker value={paymentDate} onChange={setPaymentDate} disabled={disabled} />
              </div>

              <PaymentMethodFieldsGroup
                id="line-payment-method"
                value={methodDraft}
                onChange={setMethodDraft}
                banks={banks}
                disabled={disabled}
                showDetails={false}
                allowNone={false}
                label="Payment Method"
                labelClassName="text-xs font-semibold text-foreground"
              />
            </div>

            {/* Bank field in full single row */}
            {paymentMethodUsesBank(methodDraft.paymentMethod) && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Bank</Label>
                <ExpenseBankSelect
                  value={methodDraft.bankId}
                  banks={banks}
                  onChange={(v) => setMethodDraft((d) => ({ ...d, bankId: v }))}
                  disabled={disabled}
                  placeholder="Search & select bank"
                  triggerClassName="w-full glass-input h-10"
                />
              </div>
            )}

            {/* Method Reference Details */}
            {methodDraft.paymentMethod === "online" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Transaction reference no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.transactionReferenceNo}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, transactionReferenceNo: e.target.value }))}
                  placeholder="e.g. TXN-123456"
                  disabled={disabled}
                />
              </div>
            )}

            {methodDraft.paymentMethod === "cheque" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Cheque number</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.chequeNo ?? ""}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, chequeNo: e.target.value }))}
                  placeholder="e.g. 0048291"
                  disabled={disabled}
                />
              </div>
            )}

            {methodDraft.paymentMethod === "pay_order" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Pay order no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.payOrderNo}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, payOrderNo: e.target.value }))}
                  placeholder="e.g. PO-98765"
                  disabled={disabled}
                />
              </div>
            )}

            {methodDraft.paymentMethod === "cash_deposit" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Deposit slip no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.depositSlipNo ?? ""}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, depositSlipNo: e.target.value }))}
                  placeholder="e.g. DEP-4412"
                  disabled={disabled}
                />
              </div>
            )}

            {/* Token Specific Fields (only shown when Token Payment is selected) */}
            {mode === "token" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">Token Type</Label>
                  <Select
                    value={tokenType || "unconditional"}
                    onValueChange={(v) => setTokenType(v as SaleTokenType)}
                    disabled={disabled}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="unconditional">Un-Conditional</SelectItem>
                      <SelectItem value="conditional">Conditional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">Due Date (Balance)</Label>
                  <DatePartsPicker
                    value={payableDate}
                    onChange={setPayableDate}
                    fromYear={currentYear}
                    toYear={currentYear + 10}
                    placeholder="Select due date"
                    disabled={disabled}
                  />
                </div>
              </div>
            )}

            {/* Installment Specific Fields (only when configuring entire installment sale from cash mode) */}
            {mode === "installment" && !isInstallmentSale && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">Tenure (Months)</Label>
                  <Select
                    value={String(months)}
                    onValueChange={(v) => setMonths(Number(v))}
                    disabled={disabled}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue placeholder="Select months" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {[1, 2, 3, 4, 5, 6, 9, 12, 18, 24, 36, 48].map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {m} {m === 1 ? "Month" : "Months"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">First Due Date *</Label>
                  <DatePartsPicker
                    value={firstDueDate}
                    onChange={setFirstDueDate}
                    fromYear={currentYear}
                    toYear={currentYear + 10}
                    placeholder="First due date"
                    disabled={disabled}
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="line-payment-notes" className="text-xs font-semibold text-foreground">
                Notes (Optional)
              </Label>
              <Textarea
                id="line-payment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Payment notes or instructions"
                rows={2}
                disabled={disabled}
              />
            </div>

            {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
          </div>

          {/* Right Column: Live Balance & Financial Summary Card */}
          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <Calculator className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">
                  Payment & Balance Summary
                </h4>
              </div>

              {/* Progress Bar */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs font-semibold">
                  <span className="text-muted-foreground">Payment Progress</span>
                  <span className="text-primary font-bold">{paidPercent}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted/60 overflow-hidden">
                  <div
                    className={cn(
                      "h-full transition-all duration-300",
                      paidPercent >= 100 ? "bg-emerald-500" : "bg-primary"
                    )}
                    style={{ width: `${paidPercent}%` }}
                  />
                </div>
              </div>

              {/* Financial Metrics */}
              <div className="space-y-2 pt-2 text-xs">
                <div className="flex justify-between items-center py-1 border-b border-border/30">
                  <span className="text-muted-foreground">{isInstallmentSale ? "Target Down Payment" : "Total Sale Price"}</span>
                  <span className="font-bold text-foreground text-sm">{formatCurrency(salePrice)}</span>
                </div>

                <div className="flex justify-between items-center py-1 border-b border-border/30">
                  <span className="text-muted-foreground">
                    {mode === "full" ? "Amount Received" : "This Payment Line"}
                  </span>
                  <span className="font-bold text-primary text-sm">
                    {formatCurrency(enteredAmount)}
                  </span>
                </div>

                <div className="flex justify-between items-center py-1.5">
                  <span className="font-bold text-foreground">{isInstallmentSale ? "Remaining Down Payment" : "Projected Balance"}</span>
                  <span
                    className={cn(
                      "font-bold text-base tabular-nums",
                      projectedRemainingBalance > 0
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-emerald-600 dark:text-emerald-400"
                    )}
                  >
                    {formatCurrency(projectedRemainingBalance)}
                  </span>
                </div>
              </div>

              {/* Informational Alert Box */}
              {projectedRemainingBalance > 0 ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs space-y-1">
                  <p className="font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5" />
                    {isInstallmentSale ? `Remaining Down Payment: ${formatCurrency(projectedRemainingBalance)}` : `Remaining Balance: ${formatCurrency(projectedRemainingBalance)}`}
                  </p>
                  <p className="text-muted-foreground text-[11px]">
                    {isInstallmentSale
                      ? "Remaining down payment due today. Financed balance is distributed across monthly installments."
                      : mode === "installment"
                      ? `Balance will be financed across ${months} monthly installments of ~${formatCurrency(
                          Math.round((projectedRemainingBalance / months) * 100) / 100
                        )}/mo.`
                      : payableDate
                      ? `Balance payable on or before ${payableDate} (${tokenType}).`
                      : "Balance payable on delivery / agreement date."}
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs space-y-1">
                  <p className="font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {isInstallmentSale ? "Down Payment Fully Covered" : "100% Paid in Full"}
                  </p>
                  <p className="text-muted-foreground text-[11px]">
                    {isInstallmentSale
                      ? "Total upfront down payment is covered by payment lines. Installments will cover the financed balance."
                      : "Zero balance remaining. Gate pass and delivery can be issued immediately."}
                  </p>
                </div>
              )}
            </div>

            {/* Bottom summary note */}
            <div className="pt-3 border-t border-primary/10 text-[11px] text-muted-foreground">
              <span>Method: </span>
              <strong className="text-foreground uppercase">
                {methodDraft.paymentMethod || "CASH"}
              </strong>
              {methodDraft.bankId ? (
                <span> ({banks.find((b) => String(b.id) === methodDraft.bankId)?.name || "Bank"})</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="border-t border-border/40 pt-4 flex flex-col sm:flex-row items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" variant="premium" onClick={handleSave}>
            {editingEntry ? "Save Changes" : "Apply Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
